import { z } from 'zod';
import { Id } from '../primitives.js';
import { Kopecks } from './finance.js';
import { AppointmentKind } from './scheduling.js';

/**
 * Instruments: инструментарий, оборудование и обработка (веха 3.5).
 *
 * Документация модуля: docs/03-modules/15-instruments.md
 */

export const INSTRUMENT_LIFECYCLE_STATUS = ['sterile', 'issued', 'contaminated', 'processing', 'sterilizing', 'written_off'] as const;
export const InstrumentLifecycleStatus = z.enum(INSTRUMENT_LIFECYCLE_STATUS);
export type InstrumentLifecycleStatus = z.infer<typeof InstrumentLifecycleStatus>;

export const EQUIPMENT_LIFECYCLE_STATUS = ['in_service', 'needs_repair', 'under_repair', 'decommissioned'] as const;
export const EquipmentLifecycleStatus = z.enum(EQUIPMENT_LIFECYCLE_STATUS);
export type EquipmentLifecycleStatus = z.infer<typeof EquipmentLifecycleStatus>;

export const InstrumentType = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  processingMethod: z.string().min(1).max(200),
  maxCycles: z.number().int().positive().nullable(),
  isActive: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type InstrumentType = z.infer<typeof InstrumentType>;

export const CreateInstrumentTypeRequest = z.object({
  name: z.string().min(1).max(200),
  processingMethod: z.string().min(1).max(200),
  maxCycles: z.number().int().positive().nullable().default(null),
});
export type CreateInstrumentTypeRequest = z.infer<typeof CreateInstrumentTypeRequest>;

export const UpdateInstrumentTypeRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  processingMethod: z.string().min(1).max(200).optional(),
  maxCycles: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateInstrumentTypeRequest = z.infer<typeof UpdateInstrumentTypeRequest>;

export const Instrument = z.object({
  id: Id,
  instrumentTypeId: Id,
  inventoryNumber: z.string().min(1).max(100),
  cyclesUsed: z.number().int().nonnegative(),
  status: InstrumentLifecycleStatus,
  currentKitId: Id.nullable(),
  commissionedAt: z.iso.datetime(),
  writeOffReason: z.string().max(500).nullable(),
  writtenOffAt: z.iso.datetime().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type Instrument = z.infer<typeof Instrument>;

export const RegisterInstrumentRequest = z.object({
  instrumentTypeId: Id,
  inventoryNumber: z.string().min(1).max(100),
  commissionedAt: z.iso.datetime(),
});
export type RegisterInstrumentRequest = z.infer<typeof RegisterInstrumentRequest>;

export const UpdateInstrumentRequest = z.object({
  inventoryNumber: z.string().min(1).max(100).optional(),
});
export type UpdateInstrumentRequest = z.infer<typeof UpdateInstrumentRequest>;

export const KitTemplateLine = z.object({
  instrumentTypeId: Id,
  quantity: z.number().int().positive(),
});
export type KitTemplateLine = z.infer<typeof KitTemplateLine>;

export const KitTemplate = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  typicalCycleMinutes: z.number().int().positive(),
  isActive: z.boolean(),
  lines: z.array(KitTemplateLine),
  rowVersion: z.number().int().nonnegative(),
});
export type KitTemplate = z.infer<typeof KitTemplate>;

export const CreateKitTemplateRequest = z.object({
  name: z.string().min(1).max(200),
  typicalCycleMinutes: z.number().int().positive().default(90),
  lines: z.array(KitTemplateLine).min(1),
});
export type CreateKitTemplateRequest = z.infer<typeof CreateKitTemplateRequest>;

export const UpdateKitTemplateRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  typicalCycleMinutes: z.number().int().positive().optional(),
  isActive: z.boolean().optional(),
  lines: z.array(KitTemplateLine).min(1).optional(),
});
export type UpdateKitTemplateRequest = z.infer<typeof UpdateKitTemplateRequest>;

export const KitRequirement = z.object({
  id: Id,
  appointmentKind: AppointmentKind,
  kitTemplateId: Id,
  quantity: z.number().int().positive(),
  rowVersion: z.number().int().nonnegative(),
});
export type KitRequirement = z.infer<typeof KitRequirement>;

export const SetKitRequirementRequest = z.object({
  appointmentKind: AppointmentKind,
  kitTemplateId: Id,
  quantity: z.number().int().positive(),
});
export type SetKitRequirementRequest = z.infer<typeof SetKitRequirementRequest>;

export const PackagingSterilityRule = z.object({
  packagingType: z.string().min(1).max(100),
  sterileDays: z.number().int().positive(),
});
export type PackagingSterilityRule = z.infer<typeof PackagingSterilityRule>;

export const SetPackagingSterilityRuleRequest = PackagingSterilityRule;
export type SetPackagingSterilityRuleRequest = z.infer<typeof SetPackagingSterilityRuleRequest>;

export const Kit = z.object({
  id: Id,
  kitTemplateId: Id,
  status: InstrumentLifecycleStatus,
  packagingType: z.string().nullable(),
  sterilizedAt: z.iso.datetime().nullable(),
  sterileUntil: z.iso.datetime().nullable(),
  /** Начало текущего цикла обработки — для оценки «успевает ли» без сети. */
  contaminatedAt: z.iso.datetime().nullable().default(null),
  currentChairId: Id.nullable(),
  writeOffReason: z.string().max(500).nullable(),
  writtenOffAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type Kit = z.infer<typeof Kit>;

export const AssembleKitRequest = z.object({
  kitTemplateId: Id,
  instrumentIds: z.array(Id),
});
export type AssembleKitRequest = z.infer<typeof AssembleKitRequest>;

/** Тело записи выдачи/возврата в очереди устройства (§6 спеки: обе стороны цикла — офлайн-совместимы). */
export const IssueKitRequest = z.object({
  kind: z.literal('issue'),
  kitId: Id,
  chairId: Id,
  appointmentId: Id.nullable().default(null),
});
export type IssueKitRequest = z.infer<typeof IssueKitRequest>;

export const ReturnKitRequest = z.object({
  kind: z.literal('return'),
  kitId: Id,
});
export type ReturnKitRequest = z.infer<typeof ReturnKitRequest>;

export const KitIssueChange = z.discriminatedUnion('kind', [IssueKitRequest, ReturnKitRequest]);
export type KitIssueChange = z.infer<typeof KitIssueChange>;

export const PROCESSING_STAGE = ['cleaning', 'disinfection', 'packaging'] as const;
export const ProcessingStage = z.enum(PROCESSING_STAGE);
export type ProcessingStage = z.infer<typeof ProcessingStage>;

/** Тело записи этапа обработки в очереди устройства. */
export const RecordProcessingStageRequest = z.object({
  kitId: Id,
  stage: z.string().min(1).max(100),
  agent: z.string().max(200).nullable().default(null),
});
export type RecordProcessingStageRequest = z.infer<typeof RecordProcessingStageRequest>;

export const KitIssue = z.object({
  id: Id,
  kitId: Id,
  kind: z.enum(['issue', 'return']),
  chairId: Id.nullable(),
  appointmentId: Id.nullable(),
  actorId: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type KitIssue = z.infer<typeof KitIssue>;

export const ProcessingRecord = z.object({
  id: Id,
  kitId: Id,
  stage: z.string(),
  agent: z.string().nullable(),
  actorId: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type ProcessingRecord = z.infer<typeof ProcessingRecord>;

export const STERILIZER_LOAD_STATUS = ['open', 'closed_ok', 'closed_failed'] as const;
export const SterilizerLoadStatus = z.enum(STERILIZER_LOAD_STATUS);
export type SterilizerLoadStatus = z.infer<typeof SterilizerLoadStatus>;

export const SterilizerLoad = z.object({
  id: Id,
  sterilizerId: Id,
  mode: z.string(),
  status: SterilizerLoadStatus,
  packagingType: z.string().nullable(),
  chemicalIndicatorResult: z.string().nullable(),
  biologicalIndicatorResult: z.string().nullable(),
  validationOverdue: z.boolean(),
  openedBy: Id,
  closedBy: Id.nullable(),
  openedAt: z.iso.datetime(),
  closedAt: z.iso.datetime().nullable(),
  kitIds: z.array(Id),
  rowVersion: z.number().int().nonnegative(),
});
export type SterilizerLoad = z.infer<typeof SterilizerLoad>;

/** Тело записи открытия загрузки в очереди устройства. */
export const OpenSterilizerLoadRequest = z.object({
  sterilizerId: Id,
  mode: z.string().min(1).max(100),
  kitIds: z.array(Id).min(1),
});
export type OpenSterilizerLoadRequest = z.infer<typeof OpenSterilizerLoadRequest>;

/** Закрытие загрузки — принципиально онлайн (результат контроля решает судьбу нескольких комплектов разом). */
export const CloseSterilizerLoadRequest = z.object({
  packagingType: z.string().min(1).max(100),
  chemicalIndicatorResult: z.enum(['passed', 'failed']),
  biologicalIndicatorResult: z.enum(['passed', 'failed', 'pending']).nullable().default(null),
});
export type CloseSterilizerLoadRequest = z.infer<typeof CloseSterilizerLoadRequest>;

export const Equipment = z.object({
  id: Id,
  inventoryNumber: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  model: z.string().max(200).nullable(),
  chairId: Id.nullable(),
  status: EquipmentLifecycleStatus,
  commissionedAt: z.iso.datetime(),
  warrantyUntil: z.iso.datetime().nullable(),
  nextServiceAt: z.iso.datetime().nullable(),
  decommissionReason: z.string().max(500).nullable(),
  decommissionedAt: z.iso.datetime().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type Equipment = z.infer<typeof Equipment>;

export const CreateEquipmentRequest = z.object({
  inventoryNumber: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  model: z.string().max(200).nullable().default(null),
  chairId: Id.nullable().default(null),
  commissionedAt: z.iso.datetime(),
  warrantyUntil: z.iso.datetime().nullable().default(null),
  sterilizer: z.object({ modes: z.string().min(1).max(500), validUntil: z.iso.datetime().nullable().default(null) }).nullable().default(null),
});
export type CreateEquipmentRequest = z.infer<typeof CreateEquipmentRequest>;

export const UpdateEquipmentRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  model: z.string().max(200).nullable().optional(),
  chairId: Id.nullable().optional(),
  warrantyUntil: z.iso.datetime().nullable().optional(),
});
export type UpdateEquipmentRequest = z.infer<typeof UpdateEquipmentRequest>;

export const Sterilizer = z.object({
  equipmentId: Id,
  modes: z.string(),
  validUntil: z.iso.datetime().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type Sterilizer = z.infer<typeof Sterilizer>;

export const UpdateSterilizerRequest = z.object({
  modes: z.string().min(1).max(500).optional(),
  validUntil: z.iso.datetime().nullable().optional(),
});
export type UpdateSterilizerRequest = z.infer<typeof UpdateSterilizerRequest>;

/** Заявка на ремонт — любая роль клиники (§2 спеки: намеренно шире необходимого). */
export const RequestRepairRequest = z.object({ reason: z.string().min(1).max(1000) });
export type RequestRepairRequest = z.infer<typeof RequestRepairRequest>;

export const MAINTENANCE_KIND = ['repair', 'maintenance', 'inspection'] as const;
export const MaintenanceKind = z.enum(MAINTENANCE_KIND);
export type MaintenanceKind = z.infer<typeof MaintenanceKind>;

/**
 * Тело записи оборудования в очереди устройства — заявка на ремонт, начало и
 * завершение работ: три перехода одного статуса `Equipment`, один
 * дискриминированный писатель, не три (§6 спеки: все три — офлайн-совместимы).
 */
export const RequestRepairChange = z.object({ change: z.literal('request_repair'), equipmentId: Id, reason: z.string().min(1).max(1000) });
export type RequestRepairChange = z.infer<typeof RequestRepairChange>;

export const StartMaintenanceChange = z.object({
  change: z.literal('start_maintenance'),
  equipmentId: Id,
  kind: MaintenanceKind,
  reason: z.string().max(1000).nullable().default(null),
  performedBy: z.string().max(200).nullable().default(null),
});
export type StartMaintenanceChange = z.infer<typeof StartMaintenanceChange>;

export const FinishMaintenanceChange = z.object({
  change: z.literal('finish_maintenance'),
  equipmentId: Id,
  costKopecks: Kopecks.nullable().default(null),
  nextServiceAt: z.iso.datetime().nullable().default(null),
});
export type FinishMaintenanceChange = z.infer<typeof FinishMaintenanceChange>;

export const EquipmentChange = z.discriminatedUnion('change', [RequestRepairChange, StartMaintenanceChange, FinishMaintenanceChange]);
export type EquipmentChange = z.infer<typeof EquipmentChange>;

export const MaintenanceRecord = z.object({
  id: Id,
  equipmentId: Id,
  kind: MaintenanceKind,
  reason: z.string().nullable(),
  performedBy: z.string().nullable(),
  costKopecks: Kopecks.nullable(),
  downtimeMinutes: z.number().int().nonnegative().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  nextServiceAt: z.iso.datetime().nullable(),
  actorId: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type MaintenanceRecord = z.infer<typeof MaintenanceRecord>;

/** Начало ремонта/ТО — переводит оборудование в `under_repair`, гасит кресло, если оно указано (решение 4 плана вехи 3.5). */
export const StartMaintenanceRequest = z.object({
  kind: MaintenanceKind,
  reason: z.string().max(1000).nullable().default(null),
  performedBy: z.string().max(200).nullable().default(null),
});
export type StartMaintenanceRequest = z.infer<typeof StartMaintenanceRequest>;

/** Завершение — принципиально онлайн: решает, возвращать ли кресло в сетку. */
export const FinishMaintenanceRequest = z.object({
  costKopecks: Kopecks.nullable().default(null),
  nextServiceAt: z.iso.datetime().nullable().default(null),
});
export type FinishMaintenanceRequest = z.infer<typeof FinishMaintenanceRequest>;

export const EquipmentMovement = z.object({
  id: Id,
  equipmentId: Id,
  fromChairId: Id.nullable(),
  toChairId: Id.nullable(),
  actorId: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type EquipmentMovement = z.infer<typeof EquipmentMovement>;

/** `equipmentId` избыточен для HTTP (он уже в пути `/equipment/{id}/move`), но обязателен в очереди устройства — тело там ровно одно, без URL. */
export const MoveEquipmentRequest = z.object({ equipmentId: Id, toChairId: Id.nullable() });
export type MoveEquipmentRequest = z.infer<typeof MoveEquipmentRequest>;

export const WRITE_OFF_APPROVAL_STATUS = ['pending', 'approved', 'rejected'] as const;
export const WriteOffApprovalStatus = z.enum(WRITE_OFF_APPROVAL_STATUS);
export type WriteOffApprovalStatus = z.infer<typeof WriteOffApprovalStatus>;

export const InstrumentWriteOff = z.object({
  id: Id,
  instrumentId: Id,
  reason: z.string(),
  status: WriteOffApprovalStatus,
  requestedBy: Id,
  approvedBy: Id.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  rejectReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type InstrumentWriteOff = z.infer<typeof InstrumentWriteOff>;

export const RequestInstrumentWriteOffRequest = z.object({ instrumentId: Id, reason: z.string().min(1).max(1000) });
export type RequestInstrumentWriteOffRequest = z.infer<typeof RequestInstrumentWriteOffRequest>;

export const EquipmentWriteOff = z.object({
  id: Id,
  equipmentId: Id,
  reason: z.string(),
  status: WriteOffApprovalStatus,
  requestedBy: Id,
  approvedBy: Id.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  rejectReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type EquipmentWriteOff = z.infer<typeof EquipmentWriteOff>;

export const RequestEquipmentWriteOffRequest = z.object({ equipmentId: Id, reason: z.string().min(1).max(1000) });
export type RequestEquipmentWriteOffRequest = z.infer<typeof RequestEquipmentWriteOffRequest>;

/** Решение по списанию — общая форма для инструмента и оборудования (решение 8 плана вехи 3.5). */
export const ApproveWriteOffRequest = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approved') }),
  z.object({ decision: z.literal('rejected'), rejectReason: z.string().min(1).max(1000) }),
]);
export type ApproveWriteOffRequest = z.infer<typeof ApproveWriteOffRequest>;

export const PURCHASE_PLAN_STATUS = ['proposed', 'approved', 'rejected'] as const;
export const PurchasePlanStatus = z.enum(PURCHASE_PLAN_STATUS);
export type PurchasePlanStatus = z.infer<typeof PurchasePlanStatus>;

export const PurchasePlanLine = z.object({
  id: Id,
  instrumentTypeId: Id.nullable(),
  description: z.string(),
  quantity: z.number().int().positive(),
  reason: z.string().nullable(),
  status: PurchasePlanStatus,
  requestedBy: Id,
  approvedBy: Id.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type PurchasePlanLine = z.infer<typeof PurchasePlanLine>;

export const CreatePurchasePlanLineRequest = z.object({
  instrumentTypeId: Id.nullable().default(null),
  description: z.string().min(1).max(500),
  quantity: z.number().int().positive().default(1),
  reason: z.string().max(1000).nullable().default(null),
});
export type CreatePurchasePlanLineRequest = z.infer<typeof CreatePurchasePlanLineRequest>;

export const ApprovePurchasePlanLineRequest = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approved') }),
  z.object({ decision: z.literal('rejected') }),
]);
export type ApprovePurchasePlanLineRequest = z.infer<typeof ApprovePurchasePlanLineRequest>;

/** Обеспеченность приёма комплектами — панель старшей медсестры (решение 5–6 плана вехи 3.5). */
export const KitReadinessLine = z.object({
  kitTemplateId: Id,
  kitTemplateName: z.string(),
  needed: z.number().int().nonnegative(),
  sterile: z.number().int().nonnegative(),
  shortfall: z.number().int().nonnegative(),
});
export type KitReadinessLine = z.infer<typeof KitReadinessLine>;
